import { readTokens, rememberTokens, clearTokens, TOKEN_STORAGE_KEY } from './tokens';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest, ApiError } from '../api/http';
import { adminGroups } from './adminAccess';

export interface Session {
  access_token?: string;
  refresh_token?: string;
  needs_profile?: boolean;
  auth_version?: number;
  birth_year?: number;
  birth_date?: string;
  tel?: string;
  expert_id: string;
  display_name: string;
  email: string;
  role: string;
  account_roles: string[];
  admin_groups?: string[];
  permissions?: string[];
  admin_role?: string;
  adminRole?: string;
  merchant_role?: boolean;
  creator_verified?: boolean;
  pending_email?: string;
  app_handoff_token?: string;
}

interface AuthValue {
  session: Session | null;
  restoring: boolean;
  login: (identifier: string, password: string) => Promise<Session>;
  establishSession: (session: Session) => void;
  logout: () => void;
  refresh: () => Promise<void>;
  isAdmin: boolean;
  isMerchant: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);
const STORAGE_KEY = 'beaw.3889.session';

export function sessionHasAdminRole(session: Session | null) {
  if (session && Array.isArray(session.admin_groups)) return adminGroups(session).length > 0;
  if (!session) return false;
  const roles = new Set(
    [session.role, ...(session.account_roles || [])]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean),
  );
  return roles.has('admin') || roles.has('super_admin');
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as Session | null; } catch { return null; }
  });
  const [restoring, setRestoring] = useState(Boolean(session));
  const sessionRevision = useRef(0);
  const save = (value: Session | null) => {
    sessionRevision.current++;
    if (!value) {
      clearTokens();
      setSession(null);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    rememberTokens(value);
    const { app_handoff_token: _oneTimeToken, access_token: _access, refresh_token: _refresh, ...persisted } = value;
    setSession(persisted);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  };
  const refresh = async () => {
    if (!session?.expert_id) return;
    const revision = sessionRevision.current;
    try { const restored = await apiRequest<Session>(readTokens() ? '/auth/v2/me' : '/auth/session', { query: readTokens() ? {} : { expert_id: session.expert_id } }); if (sessionRevision.current === revision) save(restored); }
    catch (error) {
      // Network errors and server outages do not revoke a saved session.
      if (sessionRevision.current === revision && error instanceof ApiError &&
          (error.status === 401 || error.status === 403)) save(null);
    }
  };
  useEffect(() => { refresh().finally(() => setRestoring(false)); }, []); // restore once
  useEffect(() => { const expired = () => save(null); window.addEventListener('beaw-auth-expired', expired); return () => window.removeEventListener('beaw-auth-expired', expired); }, []);
  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY && event.key !== TOKEN_STORAGE_KEY && event.key !== null) return;
      if (event.key === TOKEN_STORAGE_KEY && event.newValue !== null) return;
      sessionRevision.current++;
      try { setSession(readTokens() ? JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') : null); }
      catch { setSession(null); }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);
  const login = async (identifier: string, password: string) => {
    const value = await apiRequest<Session>('/auth/reviewer-login', { method: 'POST', body: { email: identifier, password } });
    save(value); return value;
  };
  const roles = new Set(
    [session?.role, ...(session?.account_roles || [])]
      .map((role) => String(role || '').trim().toLowerCase())
      .filter(Boolean),
  );
  const value = useMemo<AuthValue>(() => ({
    session, restoring, login, establishSession: save, logout: () => { const tokens = readTokens(); if (tokens) void apiRequest('/auth/v2/logout', { method: 'POST', body: { refresh_token: tokens.refresh_token } }).catch(() => undefined); save(null); }, refresh,
    isAdmin: sessionHasAdminRole(session),
    isMerchant: Boolean(session?.merchant_role || roles.has('merchant')),
  }), [session, restoring]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be inside AuthProvider');
  return value;
}
